import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useIdentify } from '@hungryroot/customer';
import { type CustomerProductRating } from '@hungryroot/rest';
import { useDeleteCustomerProductRating } from '../../../hooks/useDeleteCustomerProductRating';
import { usePostCustomerProductRatings } from '../../../hooks/usePostCustomerProductRatings';
import {
  useSelfProductRating,
  invalidateQuery as invalidateSelfProductRatingQuery,
  getQueryKey as getSelfProductRatingQueryKey,
} from '../../../hooks/useSelfProductRating';
import { type useDetectChanges } from '../useDetectChanges';
import { type RatablePairingItem } from '../useRatableItems';

type UseIngredientFeedback = {
  /** The pairing/bundle the ingredient is a part of. */
  item: RatablePairingItem;

  /** The rating status for the parent thumb rating. */
  rating: boolean;

  /** The ingredient to provide feedback for. */
  ingredient: RatablePairingItem['ingredients'][number];

  /**
   * Whether or not to allow API calls. This is useful for cases where consumers of this
   * hook are waiting for dependencies to be ready before making these API calls. Disabling
   * this hook will prevent any API call from being made but will keep track of what
   * call should be made after it's been enabled since th user can still interact with
   * the UI due to the optimistic nature of the `toggled` state.
   */
  enabled: boolean;

  /** Error handler to run when the API comes across an issue. */
  onError?: () => void;

  /** Change detection to run when the ingredient is toggled. */
  changeDetection: ReturnType<typeof useDetectChanges>;
};

/**
 * Handles the logic for providing feedback on an ingredient when the user
 * toggles one of the ingredients. This hook automatically handles what
 * data to send to the API based on the ingredient as well as the rating.
 * Behind the scenes, an ingredient is just a product so this hook uses
 * the product rating endpoints.
 *
 * The `enabled` prop allows consumers to still show the optimistic UI and
 * when all dependencies are completed, the API call will be made (if any).
 */
export function useIngredientFeedback(props: UseIngredientFeedback) {
  const { item, rating, ingredient, enabled, onError, changeDetection } = props;

  const latestApiCallRef = useRef<(() => void) | null>(null);

  const {
    customerProductRatingId,
    hasRated,
    localIsActive,
    setLocalIsActive,
    postCustomerProductRatingMutate,
    deleteCustomerProductRatingMutate,
  } = useIngredientsMutations({
    onError,
  });

  // This effect is used to make API calls in the case where `enabled` goes from
  // `false` to `true`. This allows the user to continue doing what they want to
  // do on the UI while we wait for any dependencies to be ready before this hook
  // is usable.
  useEffect(() => {
    // Nothing to do here since the user hasn't done anything or has gone through
    // a series of steps that should result in no API calls.
    if (!latestApiCallRef.current) {
      return;
    }

    // Dependencies are completed, run the latest API call we should have made.
    if (enabled) {
      latestApiCallRef.current();
      latestApiCallRef.current = null;
    }
  }, [enabled]);

  const { customerData } = useIdentify();

  const { data: selfProductRatingData } = useSelfProductRating({
    params: {
      id: customerProductRatingId as number,
    },
    options: {
      enabled: hasRated && Number.isInteger(customerProductRatingId),
    },
  });

  /**
   * Automatically determines which API call to make (if any) and manages
   * optimistic updates when API calls are disabled.
   */
  const onToggle = () => {
    if (!customerData?.id) {
      return;
    }

    changeDetection.updateFoodFeedbackSecondary({
      foodFeedbackId: item.bundleId || item.pairingId,
      foodFeedbackSecondaryId: ingredient.id,
      foodFeedbackSecondaryType: 'ingredient',
      prevValue: !rating,
      newValue: rating,
    });

    const createIngredientRating = () =>
      postCustomerProductRatingMutate({
        customerId: customerData.id,
        payload: {
          product: ingredient.id,
          liked: rating,
          order: item.orderNumber,
        },
      });

    const deleteIngredientRating = (customerProductRatingId: number) =>
      deleteCustomerProductRatingMutate({
        customer_id: customerData.id,
        id: customerProductRatingId,
      });

    if (enabled) {
      if (!selfProductRatingData) {
        createIngredientRating();
      } else if (customerProductRatingId) {
        deleteIngredientRating(customerProductRatingId);
      }
    } else {
      // API calls haven't been enabled yet but the user will still be doing things.
      // Keep track of the latest API call so when the hook is enabled, we can
      // run it.
      if (!selfProductRatingData) {
        setLocalIsActive(true);
        latestApiCallRef.current = () => createIngredientRating();
      } else if (customerProductRatingId) {
        setLocalIsActive(false);
        latestApiCallRef.current = () =>
          deleteIngredientRating(customerProductRatingId);
      } else {
        latestApiCallRef.current = null;
      }
    }
  };

  return {
    // Existance of the specific customer product rating here indicates that it is active.
    toggled: selfProductRatingData ? true : localIsActive,
    onToggle,
  };
}

type UseIngredientsMutationsProps = {
  onError?: () => void;
};

function useIngredientsMutations(props: UseIngredientsMutationsProps) {
  const { onError } = props;

  // Keeps track of whether or not the user has activated their ingredients toggle
  // at least once. This is used to determine whether or not we should initally
  // fetch the latest rating from the server.
  const [hasRated, setHasRated] = useState(false);

  // Keeps track of the customer product rating ID for the current item.
  const [customerProductRatingId, setCustomerProductRatingId] = useState<
    number | null
  >(null);

  // Temporary state to keep track of whether or not the ingredient item is active.
  const [localIsActive, setLocalIsActive] = useState<boolean>(false);

  const queryClient = useQueryClient();

  const { mutate: postCustomerProductRatingMutate } =
    usePostCustomerProductRatings({
      onMutate: () => {
        setLocalIsActive(true);
      },

      onSuccess: (data) => {
        // Pre-populate the cache with the successful response.
        const queryKey = getSelfProductRatingQueryKey({ id: data.id });

        queryClient.setQueryData(queryKey, data);

        // We have a product rating ID now, update the local active state.
        setLocalIsActive(true);
        setCustomerProductRatingId(data.id);
      },

      onError: () => {
        onError?.();
        setLocalIsActive(false);
      },

      onSettled: (data) => {
        setHasRated(true);

        if (data) {
          invalidateSelfProductRatingQuery({ id: data.id, queryClient });
        }
      },
    });

  const { mutate: deleteCustomerProductRatingMutate } =
    useDeleteCustomerProductRating({
      onMutate: async () => {
        setLocalIsActive(false);

        if (customerProductRatingId) {
          const queryKey = getSelfProductRatingQueryKey({
            id: customerProductRatingId,
          });

          await queryClient.cancelQueries({ queryKey });

          const previousCustomerFoodFeedback:
            | CustomerProductRating
            | undefined = queryClient.getQueryData(queryKey);

          queryClient.setQueryData(queryKey, null);

          return { previousCustomerFoodFeedback };
        }
      },

      onSuccess: () => {
        if (customerProductRatingId) {
          const queryKey = getSelfProductRatingQueryKey({
            id: customerProductRatingId,
          });
          queryClient.removeQueries({ queryKey });
        }

        setLocalIsActive(false);
        setCustomerProductRatingId(null);
      },

      onError: (_error, _variables, context) => {
        if (context && customerProductRatingId) {
          const queryKey = getSelfProductRatingQueryKey({
            id: customerProductRatingId,
          });

          queryClient.setQueryData(
            queryKey,
            context.previousCustomerFoodFeedback
          );
        }

        onError?.();

        // Invalidate the cache to make sure the UI reflects the server state just
        // in case the user is trying to do something like de-activate a stale food
        // feedback.
        if (customerProductRatingId) {
          invalidateSelfProductRatingQuery({
            id: customerProductRatingId,
            queryClient,
          });
        }
      },
    });

  return {
    customerProductRatingId,
    hasRated,
    localIsActive,
    setLocalIsActive,
    postCustomerProductRatingMutate,
    deleteCustomerProductRatingMutate,
  };
}
